import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, RefreshCw, Bug, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [status, setStatus] = useState('initializing');

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    setDebugLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[AuthCallback ${type}] ${message}`);
  };

  const processAuth = async () => {
    try {
      setError(null);
      setStatus('processing');
      addLog('Starting auth callback processing');
      
      // Log browser info
      addLog(`Browser: ${navigator.userAgent.substring(0, 100)}...`);
      addLog(`URL: ${window.location.href.substring(0, 100)}...`);
      
      // Extract session_id from URL fragment
      const hash = location.hash;
      addLog(`URL hash: ${hash ? hash.substring(0, 50) + '...' : '(empty)'}`);
      
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');
      const errorParam = params.get('error');
      const errorDescription = params.get('error_description');

      // Log all params
      addLog(`Params: session_id=${sessionId ? 'present' : 'missing'}, error=${errorParam || 'none'}`);

      // Check for OAuth error in URL
      if (errorParam) {
        addLog(`OAuth error received: ${errorParam} - ${errorDescription}`, 'error');
        setError(errorDescription || errorParam || 'Authentication was denied');
        setStatus('error');
        return;
      }

      if (!sessionId) {
        addLog('No session_id in URL hash', 'error');
        setError('No session ID received from Google. Please try again.');
        setStatus('error');
        return;
      }

      addLog(`Session ID received: ${sessionId.substring(0, 20)}...`);
      setStatus('authenticating');
      
      // Check localStorage/sessionStorage state
      const storedUtm = sessionStorage.getItem('oauth_utm_data');
      addLog(`Stored UTM data: ${storedUtm ? 'present' : 'none'}`);
      
      addLog('Calling processGoogleCallback...');
      const startTime = Date.now();
      
      const user = await processGoogleCallback(sessionId);
      
      const duration = Date.now() - startTime;
      addLog(`Callback successful in ${duration}ms`, 'success');
      addLog(`User: ${user?.email || 'unknown'}`);
      
      setStatus('success');
      addLog('Checking for pending subscription...');
      
      // Check for pending subscription action
      const pendingSubscription = localStorage.getItem('pendingSubscription');
      const redirectPath = pendingSubscription ? '/pricing/subscription' : '/dashboard';
      addLog(`Redirecting to ${redirectPath}...`);
      
      // Small delay to show success
      setTimeout(() => {
        navigate(redirectPath, { state: { user }, replace: true });
      }, 500);
      
    } catch (err) {
      console.error('Auth callback error:', err);
      addLog(`Error: ${err.message}`, 'error');
      addLog(`Error stack: ${err.stack?.substring(0, 200) || 'no stack'}`, 'error');
      
      const message = err.message || 'Google login failed. Please try again.';
      setError(message);
      setStatus('error');
    }
  };

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) {
      addLog('Skipping - already processed (StrictMode double-call)');
      return;
    }
    hasProcessed.current = true;
    addLog('useEffect triggered, starting processAuth');
    processAuth();
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    addLog('User clicked retry', 'info');
    hasProcessed.current = false;
    setDebugLogs([]);
    await processAuth();
    setRetrying(false);
  };

  const copyDebugInfo = () => {
    const info = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      error: error,
      logs: debugLogs
    };
    navigator.clipboard.writeText(JSON.stringify(info, null, 2));
    alert('Debug info copied to clipboard!');
  };

  // Error state
  if (error || status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 max-w-lg w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Login Failed</h1>
            <p className="text-slate-600 mb-6">{error}</p>
          </div>
          
          <div className="space-y-3">
            <Button 
              onClick={handleRetry}
              disabled={retrying}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {retrying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </>
              )}
            </Button>
            
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full">
                Back to Login
              </Button>
            </Link>
          </div>
          
          {/* Debug Section */}
          <div className="mt-6 pt-6 border-t">
            <button 
              onClick={() => setShowDebug(!showDebug)}
              className="flex items-center justify-between w-full text-sm text-slate-500 hover:text-slate-700"
            >
              <span className="flex items-center gap-2">
                <Bug className="w-4 h-4" />
                Debug Information
              </span>
              {showDebug ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showDebug && (
              <div className="mt-4 space-y-3">
                <div className="bg-slate-900 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <div className="space-y-1 font-mono text-xs">
                    {debugLogs.map((log, i) => (
                      <div 
                        key={i} 
                        className={`${
                          log.type === 'error' ? 'text-red-400' : 
                          log.type === 'success' ? 'text-green-400' : 
                          'text-slate-300'
                        }`}
                      >
                        <span className="text-slate-500">[{log.timestamp}]</span> {log.message}
                      </div>
                    ))}
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyDebugInfo}
                  className="w-full"
                >
                  Copy Debug Info for Support
                </Button>
              </div>
            )}
          </div>
          
          <p className="text-sm text-slate-500 mt-4 text-center">
            Having trouble? Try using email login or contact support.
          </p>
        </div>
      </div>
    );
  }

  // Loading states
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
        <p className="text-slate-600 mb-2">
          {status === 'initializing' && 'Initializing...'}
          {status === 'processing' && 'Processing login...'}
          {status === 'authenticating' && 'Authenticating with Google...'}
          {status === 'success' && 'Success! Redirecting...'}
        </p>
        
        {/* Mini debug log during loading */}
        <div className="mt-4 text-xs text-slate-400 max-w-xs mx-auto">
          {debugLogs.slice(-3).map((log, i) => (
            <div key={i} className="truncate">{log.message}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
